<template>
  <div class="flight-pad-details">
    <div v-if="show_dates" class="flight-pad-details__title">
      Itinerario di volo
      {{ flight_offer.itineraries.length > 1 ? "dal" : "per il" }}
      <span class="semibold">{{ flight_dates }}</span>
    </div>
    <div class="flight-pad-details__blocks">
      <div
        v-for="(itinerary, itinerary_index) in flight_offer.itineraries"
        :key="itinerary_index"
      >
        <div class="flight-pad-details__collapse">
          <div
            class="d-flex justify-content-between"
            v-b-toggle="`collapse:${flight_offer.id}${itinerary_index}`"
          >
            <div class="flight-pad-details__collapse__title">
              <span
                :class="`h5 semibold text-primary m-0 ${
                  get_itinerary_delta(itinerary) > 0 ? 'mr-2' : ''
                }`"
              >
                <span>{{ itinerary.segments[0].departure.iataCode }}</span> -
                <delta-overlay
                  :text="
                    itinerary.segments[itinerary.segments.length - 1].arrival
                      .iataCode
                  "
                  :delta="get_itinerary_delta(itinerary)"
                >
                </delta-overlay>
              </span>
              <span class="d-flex align-items-center" style="gap: 0.25rem">
                <span class="d-flex align-items-center" style="gap: 0.25rem">
                  <flight-pad-stops :itinerary="itinerary" />
                  <span
                    v-if="itinerary.duration"
                    style="font-size: 40px; line-height: 0"
                    >&middot;</span
                  >
                  <flight-pad-duration
                    v-if="itinerary.duration"
                    :duration="itinerary.duration"
                    :font_size="16"
                    full
                  />
                </span>
                <img
                  class="flight-pad-details__logo"
                  :src="`https://static.vadobay.com/airlines-logos/200x100/${carrier_iata(
                    itinerary
                  )}.png`"
                  :alt="carrier_iata(itinerary)"
                />
              </span>
            </div>
            <div class="flight-pad-details__toggle">
              <vb-icon name="chevron_right"></vb-icon>
            </div>
          </div>
          <b-collapse
            :id="`collapse:${flight_offer.id}${itinerary_index}`"
            :ref="`collapse:${flight_offer.id}${itinerary_index}`"
            class="pt-3"
            :visible="!collapsed"
          >
            <flight-pad-vertical-path :itinerary="itinerary" />
          </b-collapse>
        </div>
      </div>
    </div>
    <flight-pad-details-bags :flight_offer="flight_offer" />
  </div>
</template>

<script>
import moment from "moment";
import { verbose_month } from "@liveflight/utilities/mixins"; 
import { getSeatsForSegment } from "@liveflight/utilities/seat-map";
import { carrier_iata } from "../script/utilities";
import { get_itinerary_delta } from "@liveflight/utilities/flight-duration";
import FlightPadDuration from "@liveflight/components/blocks/flight-pad/flight-pad-duration.vue";
import FlightPadStops from "@liveflight/components/blocks/flight-pad/flight-pad-stops.vue";
import FlightPadDetailsBags from "@liveflight/components/blocks/flight-pad/flight-pad-details/flight-pad-details-bags.vue";
import FlightPadVerticalPath from "@liveflight/components/blocks/flight-pad/flight-pad-details/flight-pad-vertical-path.vue";
import DeltaOverlay from "@liveflight/components/blocks/flight-pad/delta-overlay.vue";

export default {
  name: "flight-pad-details",
  components: {
    "flight-pad-duration": FlightPadDuration,
    "flight-pad-stops": FlightPadStops,
    "flight-pad-details-bags": FlightPadDetailsBags,
    "flight-pad-vertical-path": FlightPadVerticalPath,
    "delta-overlay": DeltaOverlay,
  },
  props: {
    flight_offer: Object,
    show_dates: {
      type: Boolean,
      default: () => false,
    },
    collapsed: {
      type: Boolean,
      default: () => false,
    },
  }, 
  methods: {
    getSeatsForSegment,
    get_itinerary_delta,
    verbose_month,
    carrier_iata,
  },
  computed: {
    dictionaries() {
      return this.$store.state.liveflight.results.dictionaries;
    },
    flight_dates() {
      try {
        const itineraries = this.flight_offer.itineraries || [];
        const first_segment = (itineraries[0].segments || [])[0];
        const date_start = moment(first_segment.departure.at);
        if (itineraries.length === 1) {
          return `${date_start.date()} ${verbose_month(date_start.month())}`;
        }
        const arrival = itineraries[itineraries.length - 1].segments || [];
        const last_segment = arrival[arrival.length - 1];
        const date_end = moment(last_segment.arrival.at);
        if (date_start.diff(date_end, "years") > 1) {
          return `${date_start.date()} ${verbose_month(
            date_start.month()
          )} ${date_start.year()} al ${date_end.date()} ${verbose_month(
            date_end.month()
          )} ${date_end.year()}`;
        }
        if (date_start.diff(date_end, "months") > 1) {
          return `${date_start.date()} ${verbose_month(
            date_start.month()
          )} al ${date_end.date()} ${verbose_month(date_end.month())}`;
        }
        return `${date_start.date()} al ${date_end.date()} ${verbose_month(date_end.month())}`;
      } catch {
        return "";
      }
    },
  },
};
</script>

<style lang="scss">
.flight-pad-details {
  $this: &;
  display: flex;
  flex-direction: column;
  &__title {
    font-size: 16px;
    padding-top: 30px;
    padding-bottom: 30px;
  }
  &__logo {
    max-height: 30px;
    @include media-breakpoint-down(md) {
      display: none;
    }
  }
  &__blocks {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }
  &__collapse {
    border-bottom: 2.5px dashed $light-gray;
    padding-bottom: 20px;
    &__title {
      display: flex;
      gap: 10px;
      align-items: center;
      @include media-breakpoint-down(md) {
        flex-direction: column;
        align-items: flex-start;
      }
    }
  }
  &__toggle {
    width: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
    svg {
      width: 12px;
      height: 12px;
    }
    [role="button"],
    & > div {
      transition: $transition-base;
    }
    @at-root #{$this}__collapse.not-collapsed & > div {
      transform: rotate(90deg);
    }

    @include media-breakpoint-down(md) {
      transform: rotate(90deg);
      @at-root #{$this}__collapse.not-collapsed & > div {
        transform: rotate(180deg);
      }
    }
  }

  &__description {
    color: $dark-gray;
    font-size: 12px;
  }
}
</style>
